import React, { lazy, Suspense, useEffect } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';

import { ComingSoonPage } from './ComingSoonPage.tsx';
import { MarketingLayout } from './marketing/components/MarketingLayout.tsx';
import { ROUTES } from './marketing/routes.ts';

// Every page is lazy-loaded so each route ships as its own chunk.
const HomePage = lazy(() => import('./marketing/pages/HomePage.tsx'));
const AiPage = lazy(() => import('./marketing/pages/AiPage.tsx'));
const AiGovernancePage = lazy(
  () => import('./marketing/pages/AiGovernancePage.tsx')
);
const WorkflowAutomationPage = lazy(
  () => import('./marketing/pages/WorkflowAutomationPage.tsx')
);
const SubmissionToBindPage = lazy(
  () => import('./marketing/pages/SubmissionToBindPage.tsx')
);
const CoverageTowerPage = lazy(
  () => import('./marketing/pages/CoverageTowerPage.tsx')
);
const PremiumAccountingPage = lazy(
  () => import('./marketing/pages/PremiumAccountingPage.tsx')
);
const BillingPage = lazy(() => import('./marketing/pages/BillingPage.tsx'));
const CommissionsPage = lazy(
  () => import('./marketing/pages/CommissionsPage.tsx')
);
const ComplianceBackOfficePage = lazy(
  () => import('./marketing/pages/ComplianceBackOfficePage.tsx')
);
const PortalsPage = lazy(() => import('./marketing/pages/PortalsPage.tsx'));
const PlatformPage = lazy(() => import('./marketing/pages/PlatformPage.tsx'));
const IntegrationsPage = lazy(
  () => import('./marketing/pages/IntegrationsPage.tsx')
);
const ObservabilityPage = lazy(
  () => import('./marketing/pages/ObservabilityPage.tsx')
);
const SecurityPage = lazy(() => import('./marketing/pages/SecurityPage.tsx'));
const MgaMguPage = lazy(() => import('./marketing/pages/MgaMguPage.tsx'));
const WholesaleBrokersPage = lazy(
  () => import('./marketing/pages/WholesaleBrokersPage.tsx')
);
// Public, indexable founder overview — a top-level route outside /preview.
const WhyPage = lazy(() => import('./WhyPage.tsx'));

/**
 * Scrolls to the top on route change, or to the anchor target when the URL
 * carries a hash (e.g. `#waitlist`).
 */
function ScrollManager(): null {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const target = document.getElementById(hash.slice(1));
      if (target) {
        target.scrollIntoView();
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
}

export function App(): React.ReactElement {
  return (
    <BrowserRouter>
      <ScrollManager />
      <Routes>
        {/* Public soft-launch landing — never links into /preview. */}
        <Route path='/' element={<ComingSoonPage />} />
        {/* Public, indexable founder overview — the one page that is not noindex.
            Lazy + top-level, so it needs its own Suspense boundary (the only
            other one lives inside MarketingLayout, which wraps /preview only). */}
        <Route
          path='/why'
          element={
            <Suspense fallback={null}>
              <WhyPage />
            </Suspense>
          }
        />
        {/* Unlisted marketing preview, reachable only by direct deep link. */}
        <Route path='/preview' element={<MarketingLayout />}>
          <Route index element={<HomePage />} />
          <Route path={ROUTES.ai} element={<AiPage />} />
          <Route
            path={ROUTES['ai-governance']}
            element={<AiGovernancePage />}
          />
          <Route
            path={ROUTES['workflow-automation']}
            element={<WorkflowAutomationPage />}
          />
          <Route
            path={ROUTES['submission-to-bind']}
            element={<SubmissionToBindPage />}
          />
          <Route
            path={ROUTES['coverage-tower']}
            element={<CoverageTowerPage />}
          />
          <Route
            path={ROUTES['premium-accounting']}
            element={<PremiumAccountingPage />}
          />
          <Route path={ROUTES.billing} element={<BillingPage />} />
          <Route path={ROUTES.commissions} element={<CommissionsPage />} />
          <Route
            path={ROUTES['compliance-back-office']}
            element={<ComplianceBackOfficePage />}
          />
          <Route path={ROUTES.portals} element={<PortalsPage />} />
          <Route path={ROUTES.platform} element={<PlatformPage />} />
          <Route path={ROUTES.integrations} element={<IntegrationsPage />} />
          <Route path={ROUTES.observability} element={<ObservabilityPage />} />
          <Route path={ROUTES.security} element={<SecurityPage />} />
          <Route path={ROUTES['mga-mgu']} element={<MgaMguPage />} />
          <Route
            path={ROUTES['wholesale-brokers']}
            element={<WholesaleBrokersPage />}
          />
          <Route path='*' element={<Navigate to={ROUTES.home} replace />} />
        </Route>
        <Route path='*' element={<Navigate to='/' replace />} />
      </Routes>
    </BrowserRouter>
  );
}
